/**
 * WhyChooseUs — Morgan Legal Group
 * Design: Navy background, bold numbered reasons, gold accents
 * Large background number watermarks
 */
import { useEffect, useRef, useState } from "react";

const reasons = [
  {
    number: "01",
    title: "Proven Track Record",
    description: "Over 5,000 New York families have trusted us with their most important legal matters. Our success rate and client satisfaction scores speak for themselves.",
  },
  {
    number: "02",
    title: "Personalized Strategy",
    description: "No two estates are alike. We take the time to understand your unique family dynamics, financial situation, and long-term goals before crafting your legal strategy.",
  },
  {
    number: "03",
    title: "Transparent Pricing",
    description: "We believe in clear, upfront pricing with no hidden fees. You'll always know exactly what you're paying for and why — no surprises, no billing shock.",
  },
  {
    number: "04",
    title: "Citywide Presence",
    description: "With offices serving all five boroughs and Long Island, we're always nearby. Video consultations also available for your convenience.",
  },
  {
    number: "05",
    title: "Rapid Response",
    description: "Legal matters don't wait. Our team responds to all inquiries within 24 hours, and we offer same-week appointments for urgent matters.",
  },
  {
    number: "06",
    title: "Holistic Approach",
    description: "We coordinate with your financial advisors, accountants, and other professionals to ensure your estate plan integrates seamlessly with your overall wealth strategy.",
  },
];

export default function WhyChooseUs() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 lg:py-28 bg-navy relative overflow-hidden">
      {/* Background watermark */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        <span className="absolute -top-10 right-0 font-display text-[18rem] font-bold text-white/[0.02] leading-none">
          TRUST
        </span>
      </div>

      {/* Gold top line */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={ref} className="max-w-2xl mb-14">
          <div className="section-label mb-4 text-gold">
            Why Choose Us
          </div>
          <h2 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            Six Reasons New York Families{" "}
            <span className="text-gold italic">Choose Morgan Legal</span>
          </h2>
        </div>

        {/* Reasons grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {reasons.map((reason, i) => (
            <div
              key={reason.number}
              className={`bg-navy p-8 lg:p-10 relative group hover:bg-[#1e2e50] transition-colors duration-300 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${i * 100}ms`, transition: `opacity 600ms ${i * 100}ms, transform 600ms ${i * 100}ms, background-color 300ms` }}
            >
              {/* Large number watermark */}
              <span className="absolute top-4 right-6 font-display text-6xl font-bold text-white/[0.04] select-none pointer-events-none leading-none">
                {reason.number}
              </span>

              {/* Gold accent number */}
              <div className="font-display text-gold text-sm font-bold tracking-widest mb-4 uppercase">
                — {reason.number}
              </div>

              <h3 className="font-display text-white text-xl lg:text-2xl font-bold mb-3 group-hover:text-gold transition-colors duration-200">
                {reason.title}
              </h3>

              <p className="font-body text-white/60 text-sm leading-relaxed">
                {reason.description}
              </p>

              {/* Bottom gold line on hover */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-12 text-center transition-all duration-700 delay-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="font-body text-white/60 text-base mb-6">
            Ready to protect your family&apos;s future? Let&apos;s start with a conversation.
          </p>
          <button
            onClick={() => {
              const el = document.querySelector("#consultation");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-gold"
          >
            Schedule Free Consultation
          </button>
        </div>
      </div>
    </section>
  );
}
